import React, { useEffect, useState } from 'react';
import { loadMultiRunRecord } from '../../../lib/api/runningtrackMonitor';
import {
  Card, CardHead, CardTitle, CardBody, Tools, Hint, Kpis, Kpi, TableWrap, Tbl, Empty, Chip, Rank, Who,
} from './ZetaUi';
import ZetaRangePicker from './ZetaRangePicker';
import ZetaMemberModal from './ZetaMemberModal';
import { presetRange, rangeUnix, fromUnix, md, pad, km, dur, num, mapLimit, getMemberInfo } from './zetaUtil';

// 멀티런 기록 — 기존 GET /runningtrack/multirun/record. 응답의 코드값은 서버 MR_* 표와 같다.
//   kind 1 그룹·2 레이스 / goalType 1 시간(초)·2 거리(m) / result 1 완료·2 해산·3 시간 종료 / bandID 1~4.
//   참가자 finishTime 은 밀리초(소켓 서버 MultiRunManager: 완주 시각 − 출발 시각).

const KIND = { 1: ['그룹', 'blue'], 2: ['레이스', 'purple'] };
const BAND = { 1: '걷기', 2: '빠르게 걷기', 3: '조깅', 4: '러닝' };
const ROOM_RESULT = { 1: ['완료', 'blue'], 2: ['해산', 'red'], 3: ['시간 종료', 'gray'] };
const MEMBER_RESULT = { finish: ['완주', 'blue'], unfinish: ['미완주', 'gray'], giveup: ['포기', 'red'], leave: ['나감', 'gray'], disconnect: ['연결 끊김', 'red'] };
const goalText = (r) => (Number(r.goalType) === 1 ? `${Math.round((Number(r.goalValue) || 0) / 60)}분` : `${(Number(r.goalValue) || 0) / 1000}km`);

const ZetaMultiRun = ({ clubID, socketKey }) => {
  const [range, setRange] = useState(presetRange('30'));
  const [rooms, setRooms] = useState(null);
  const [open, setOpen] = useState({});
  const [names, setNames] = useState({});
  const [openID, setOpenID] = useState(null);

  useEffect(() => {
    if (!clubID) return;
    let alive = true;
    setRooms(null);
    const { startTime, endTime } = rangeUnix(range);
    loadMultiRunRecord(clubID, startTime, endTime)
      .then((res) => {
        const list = res.data && res.data.result === 1 && Array.isArray(res.data.infos) ? res.data.infos : false;
        if (!alive) return;
        setRooms(list);
        if (!list) return;
        const ids = Array.from(new Set([].concat(...list.map((r) => (r.memberRecords || []).map((m) => m.memberID)), list.map((r) => r.hostMemberID)).filter((v) => v > 0)));
        mapLimit(ids, 6, async (id) => {
          const info = await getMemberInfo(id, clubID);
          if (alive && info) setNames((prev) => ({ ...prev, [id]: info }));
        });
      })
      .catch(() => alive && setRooms(false));
    return () => { alive = false; };
  }, [clubID, range]);

  const list = rooms || [];
  const people = list.reduce((a, r) => a + (r.memberRecords || []).length, 0);
  const fin = list.reduce((a, r) => a + (r.memberRecords || []).filter((m) => m.result === 'finish').length, 0);
  const races = list.filter((r) => Number(r.kind) === 2).length;
  const disband = list.filter((r) => Number(r.result) === 2).length;
  const nameOf = (id) => (names[id] ? names[id].name : `회원번호 ${id}`);

  return (
    <Card>
      <CardHead>
        <CardTitle><span role="img" aria-label="멀티런">👥</span> 멀티런 기록</CardTitle>
        <Tools><ZetaRangePicker range={range} onChange={setRange} idPrefix="zeta-mr" /></Tools>
      </CardHead>
      <CardBody>
        <Kpis>
          <Kpi label="방" value={rooms ? list.length : '…'} unit="개" color="#1A73E9" sub={`레이스 ${races} · 그룹 ${list.length - races}`} />
          <Kpi label="참여 연인원" value={rooms ? num(people) : '…'} unit="명" sub={`방당 평균 ${list.length ? (people / list.length).toFixed(1) : 0}명`} />
          <Kpi label="완주율" value={people ? Math.round((fin / people) * 100) : 0} unit="%" sub={`${fin}명 완주`} />
          <Kpi label="해산된 방" value={rooms ? disband : '…'} unit="개" sub="인원 부족 등" />
        </Kpis>
        <Hint>방장 앱이 저장한 결과입니다. 행을 누르면 참가자 결과가 펼쳐지고, 참가자를 누르면 회원 상세가 열립니다.</Hint>
        <TableWrap>
          <Tbl>
            <thead><tr><th /><th>시작</th><th>종류</th><th>목표</th><th>강도</th><th className="r">인원</th><th className="r">진행 시간</th><th>결과</th><th>방장</th></tr></thead>
            <tbody>
              {list.map((r) => {
                const id = r.runningTrackMultiRunRecordID;
                const isOpen = !!open[id];
                const st = fromUnix(r.startTime);
                const kind = KIND[r.kind] || ['-', 'gray'];
                const res = ROOM_RESULT[r.result] || ['-', 'gray'];
                const members = (r.memberRecords || []).slice().sort((a, b) => (a.raceRank || 99) - (b.raceRank || 99) || b.distance - a.distance);
                return (
                  <React.Fragment key={id}>
                    <tr className="click" aria-expanded={isOpen} onClick={() => setOpen({ ...open, [id]: !isOpen })}>
                      <td style={{ color: '#868e96' }}>{isOpen ? '▾' : '▸'}</td>
                      <td>{md(st)} {pad(st.getHours())}:{pad(st.getMinutes())}</td>
                      <td><Chip tone={kind[1]}>{kind[0]}</Chip></td>
                      <td>{goalText(r)}</td>
                      <td className="muted">{BAND[r.bandID] || '-'}</td>
                      <td className="r">{r.memberCount || members.length}명</td>
                      <td className="r">{dur(r.duration)}</td>
                      <td><Chip tone={res[1]}>{res[0]}</Chip></td>
                      <td>{r.hostMemberID ? nameOf(r.hostMemberID) : '-'}</td>
                    </tr>
                    {isOpen ? (
                      <tr className="detail">
                        <td colSpan={9}>
                          <TableWrap>
                            <Tbl>
                              <thead><tr><th>순위</th><th>이름</th><th>아이디</th><th>회원번호</th><th className="r">거리</th><th className="r">완주 시간</th><th>결과</th><th className="r">포인트</th></tr></thead>
                              <tbody>
                                {members.map((m) => {
                                  const mr = MEMBER_RESULT[m.result] || [m.result || '-', 'gray'];
                                  const info = names[m.memberID];
                                  return (
                                    <tr key={m.memberID} className="click" onClick={(e) => { e.stopPropagation(); setOpenID(m.memberID); }}>
                                      <td>{m.raceRank ? <Rank n={m.raceRank}>{m.raceRank}</Rank> : <span style={{ color: '#868e96' }}>-</span>}</td>
                                      <td><Who name={info ? info.name : ''} main={info ? info.name : '…'} /></td>
                                      <td style={{ fontFamily: 'Roboto Mono, monospace', fontSize: 12.5 }}>{info ? info.account || '-' : '…'}</td>
                                      <td className="muted">{m.memberID}</td>
                                      <td className="r">{km(m.distance)}</td>
                                      <td className="r">{m.finishTime ? dur(m.finishTime / 1000) : '-'}</td>
                                      <td><Chip tone={mr[1]}>{mr[0]}</Chip></td>
                                      <td className="r">{m.resultPoint ? `${num(m.resultPoint)}P` : '-'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Tbl>
                          </TableWrap>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Tbl>
          {rooms === null ? <Empty>불러오는 중…</Empty> : rooms === false ? <Empty>멀티런 기록을 불러오지 못했습니다</Empty> : !list.length ? <Empty>이 기간에 멀티런 기록이 없습니다</Empty> : null}
        </TableWrap>
      </CardBody>
      <ZetaMemberModal open={!!openID} memberID={openID} clubID={clubID} socketKey={socketKey} onClose={() => setOpenID(null)} />
    </Card>
  );
};

export default ZetaMultiRun;
